import {
  PAYMENT_TYPES,
  PAYMENT_TYPE_LABEL,
  toCents,
  toDecimal,
  type JobExpense,
} from "@roof-hawk/core";
import { useState } from "react";

import { Modal } from "../../components/ui";
import { t } from "../../i18n";

/**
 * One expense on a job.
 *
 * Most of this form is destined for QuickBooks — the name, the amount, how it
 * was paid, which trade it belongs to and the tracking number off the invoice.
 * The note is the exception: it is our own working, the record of how a figure
 * was arrived at, and it stays here.
 *
 * The change order flag is the one that matters for reporting. An expense the
 * customer agreed to is not the same thing as one we failed to price, and
 * "Added After Scheduling" only means anything if the two are kept apart.
 *
 * **It saves as you go.** Jonathon, 2026-08-19: "everything needs to auto
 * save." A box saves when you leave it and a dropdown when you change it —
 * never on every keystroke, which is the same rule the order editors' cells
 * already follow. So there is no Save button and no way to lose what was
 * typed: closing the form is just closing it.
 */
export function ExpenseModal({
  expense,
  trades,
  onClose,
  onSave,
}: {
  expense: JobExpense;
  trades: readonly string[];
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const [name, setName] = useState(expense.name ?? "");
  const [amount, setAmount] = useState(String(expense.amount ?? "0"));
  const [paymentType, setPaymentType] = useState(
    expense.payment_type ?? "Cash",
  );
  const [trade, setTrade] = useState(expense.trade ?? "");
  const [tracking, setTracking] = useState(expense.tracking_number ?? "");
  const [isChangeOrder, setIsChangeOrder] = useState(
    Boolean(expense.is_change_order),
  );
  const [note, setNote] = useState(expense.note ?? "");
  const [busy, setBusy] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  /*
   * What is in the database now, as far as this form knows.
   *
   * Not the `expense` prop: that is the row as it was when the form opened and
   * it never changes again, so comparing against it would decide that typing a
   * value, saving it and then typing the original back is "no change" — and
   * leave the edit standing.
   */
  const [saved, setSaved] = useState({
    name: expense.name ?? "",
    amount: Number(expense.amount ?? 0),
    tracking: expense.tracking_number ?? "",
    note: expense.note ?? "",
  });

  /** One field, written on its own, as soon as it is left. */
  async function save(
    patch: Record<string, unknown>,
    remember?: Partial<typeof saved>,
  ) {
    setBusy(true);
    try {
      await onSave(patch);
      if (remember) setSaved((was) => ({ ...was, ...remember }));
      setSavedOnce(true);
    } finally {
      setBusy(false);
    }
  }

  // A retired code still shows if the row already carries it.
  const options = PAYMENT_TYPES.includes(paymentType)
    ? PAYMENT_TYPES
    : [paymentType, ...PAYMENT_TYPES];

  return (
    <Modal
      title={t("Expense")}
      subtitle={t(
        "Saves as you go. Everything here goes to QuickBooks except the note.",
      )}
      onClose={onClose}
      actions={
        <button className="btn-primary" onClick={onClose}>
          {t("Done")}
        </button>
      }
    >
      <div className="fgrid one">
        <div className="field">
          <label>{t("Name")}</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => {
              if (name === saved.name) return;
              void save({ name: name || null }, { name });
            }}
          />
        </div>

        <div className="field">
          <label>{t("Amount")}</label>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => {
              // Something that is not a number is not saved as zero. The box
              // keeps what was typed so it can be corrected.
              if (!Number.isFinite(Number(amount.trim()))) return;
              const value = toDecimal(toCents(amount));
              if (value === saved.amount) return;
              void save({ amount: value }, { amount: value });
            }}
          />
        </div>

        <div className="field">
          <label>{t("Payment Type")}</label>
          <select
            value={paymentType}
            onChange={(e) => {
              const value = e.target.value as typeof paymentType;
              setPaymentType(value);
              void save({ payment_type: value });
            }}
          >
            {options.map((p) => (
              <option key={p} value={p}>
                {t(PAYMENT_TYPE_LABEL[p] ?? p)}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>{t("Trade")}</label>
          <select
            value={trade}
            onChange={(e) => {
              setTrade(e.target.value);
              void save({ trade: e.target.value || null });
            }}
          >
            <option value="">—</option>
            {trades.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>{t("Tracking Number")}</label>
          <input
            placeholder={t("Invoice or receipt reference")}
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            onBlur={() => {
              if (tracking === saved.tracking) return;
              void save({ tracking_number: tracking || null }, { tracking });
            }}
          />
        </div>

        <div className="field">
          <label>{t("Change Order")}</label>
          <div className="togline">
            <button
              className={`tog${isChangeOrder ? " on" : ""}`}
              onClick={() => {
                const value = !isChangeOrder;
                setIsChangeOrder(value);
                void save({ is_change_order: value });
              }}
            />
            <span className="na-hint">
              {isChangeOrder
                ? t(
                    "Agreed with the customer — kept out of the added-after-scheduling figure.",
                  )
                : t("Counts as something the estimate missed.")}
            </span>
          </div>
        </div>

        <div className="field">
          <label>{t("Note")}</label>
          <textarea
            rows={4}
            placeholder={t("How this figure was arrived at. Internal only.")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => {
              if (note === saved.note) return;
              void save({ note: note || null }, { note });
            }}
          />
        </div>

        <div className="na-hint" role="status">
          {busy
            ? t("Saving…")
            : savedOnce
              ? t("Saved")
              : t("Changes save on their own.")}
        </div>
      </div>
    </Modal>
  );
}
